import { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { CheckCircle2, Zap } from "lucide-react";
import { trackCallClick } from "../lib/tracking";

export default function HeroSection() {
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleVideoClick = () => {
    if (isMobile) return;
    const v = videoRef.current;
    if (v) {
      if (v.paused) v.play();
      else v.pause();
    }
  };

  return (
    <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-gradient-to-br from-blue-50/40 via-background to-blue-100/30">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-blue-400/5 pointer-events-none"></div>
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <a href="https://www.credly.com/badges/ec493c9a-27ca-4f69-8176-381b185b47e3" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-slate-100/70 via-white/80 to-slate-200/70 hover:from-slate-200/80 hover:to-slate-300/80 border border-slate-300 rounded-lg text-xs sm:text-sm font-medium cursor-pointer shadow-sm hover:shadow-md hover:scale-105 transition-transform duration-300">
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 bg-gradient-to-br from-white via-slate-200 to-slate-100 rounded p-1 sm:p-1.5 group-hover:rotate-3 transition-transform duration-300">
                  <img src="/Certif/Apple Certified Support Professional (1).png" alt="Apple Certified" className="w-full h-full object-contain" />
                </div>
                <span className="text-slate-800 font-semibold group-hover:text-primary transition-colors whitespace-nowrap">Certifié par Apple</span>
              </a>
              <a href="https://www.trustpilot.com/review/macsupport-solution.com" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-slate-100/70 via-white/80 to-slate-200/70 hover:from-slate-200/80 hover:to-slate-300/80 border border-slate-300 rounded-lg text-xs sm:text-sm font-medium cursor-pointer shadow-sm hover:shadow-md hover:scale-105 transition-transform duration-300">
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded border border-slate-300 p-1 sm:p-1.5 group-hover:rotate-3 transition-transform duration-300">
                  <img src="/Certif/trustpilot.png" alt="Trustpilot" className="w-full h-full object-contain" />
                </div>
                <span className="text-slate-800 font-semibold group-hover:text-primary transition-colors whitespace-nowrap">Trustpilot</span>
              </a>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Mac Support Solution : <br /> La solution idéale pour une gestion fluide et sans interruption de votre parc informatique.
            </h1>

            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Experts certifiés Apple, nous assurons la gestion et la maintenance proactive de votre parc, pour une activité sans interruption.
            </p>

            <div className="space-y-3">
              {["Maintenance proactive 24/7", "Intervention directement dans vos locaux", "Zéro interruption de votre activité"].map((t) => (
                <div key={t} className="flex items-center gap-3 group/item">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover/item:bg-primary/20 transition-colors">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  </div>
                  <span className="text-foreground group-hover/item:translate-x-1 transition-transform">{t}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+33189707560" onClick={() => trackCallClick("hero")} className="inline-flex items-center justify-center rounded-md text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8 py-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                +33 1 89 70 75 60
              </a>
              <Button size="lg" variant="outline" className="text-base bg-transparent hover:bg-accent hover:scale-105 transition-transform duration-300" onClick={() => document.getElementById('services')?.scrollIntoView({behavior:'smooth'})}>
                Découvrir nos services
              </Button>
            </div>
          </div>

          <div className="relative lg:pl-8">
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-muted border border-border shadow-2xl">
              <video
                ref={videoRef}
                className="w-full h-full object-cover cursor-pointer"
                loop
                playsInline
                controls
                controlsList="nodownload nofullscreen noremoteplayback"
                poster="/Certif/coverimagehero.png"
                onClick={handleVideoClick}
              >
                <source src="https://pub-5af263c527e34dbf960459c0b6615449.r2.dev/Vide%CC%81o%20support%20Mac.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none"></div>
            </div>

            <div className="hidden md:block absolute -top-6 -left-6 bg-card/90 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 max-w-[240px]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg flex items-center justify-center ring-2 ring-primary/20">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground leading-none">95%</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">des pannes résolues le jour même</div>
                </div>
              </div>
            </div>

            <div className="hidden md:block absolute -bottom-6 -right-6 bg-card/90 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-lg flex items-center justify-center ring-2 ring-secondary/20">
                  <Zap className="h-6 w-6 text-secondary animate-pulse" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">-2h</div>
                  <div className="text-sm text-muted-foreground">Temps réponse</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
